import React, { useState, useCallback, useEffect } from 'react';
import { Grid, Box, Typography, Divider, Avatar } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { format, parseISO, getYear } from 'date-fns';
import { CustomButton } from '../../shared';
import { Mail, PrescribersIcon, Upload, Computer, NoImage } from '../../../assets';
import { useSelector, useDispatch } from 'react-redux';
import { updateProfileDetails, updatePassword, userLoginActivity } from '../../../redux/thunks/userThunks';

const personalInfoSchema = yup.object({
    name: yup.string(),
    firstName: yup.string()
        .required('First Name is required')
        .matches(/^[a-zA-Z]+$/, 'First Name should only contain letters'),

    lastName: yup.string()
        .required('Last Name is required')
        .matches(/^[a-zA-Z]+$/, 'Last Name should only contain letters'),

    email: yup.string()
        .required('Email is required')
        .email('Invalid email address'),

    profilePic: yup.mixed().notRequired(),
    profileUrl: yup.mixed().notRequired()
}).required();

const passwordChange = {
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password'
};

const passwordChangeSchema = yup.object({
    currentPassword: yup.string().required('To change password, current Password is required'),
    newPassword: yup
        .string()
        .required('New Password Required')
        .min(12, 'Your new password must be more than 12 characters.')
        .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#+])[A-Za-z\d@$!%*?&#+]+$/,
            'Your new password must contain at least one lowercase letter, one uppercase letter, one number, and one special character.'
        )
        .notOneOf([yup.string().matches(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi)], 'Your password cannot contain <script> tags.'),
    confirmPassword: yup
        .string()
        .required('Confirm Password is required')
        .oneOf([yup.ref('newPassword'), null], 'Confirm Password must match New Password') 
});

function Profile() {
    const dispatch = useDispatch();
    const { 
        userDetails, 
        userLoginActivity: loginActivity
    } = useSelector((state) => state.user);
    const { company } = useSelector((state) => state.company);
    const { is_admin } = userDetails;
    const [isCanceled, setIsCanceled] = useState(false);
    const {
        register: personalInfoRegister,
        handleSubmit: handlePersonalInfoSubmit,
        setValue: setPersonalInfo,
        getValues,
        watch,
        formState: { errors: personalInfoErrors }
    } = useForm({
        resolver: yupResolver(personalInfoSchema),
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            profilePic: null
        }
    });
    const userProfileUrl = watch('profileUrl');

    const {
        register: passwordChangeRegister,
        handleSubmit: handlePasswordChangeSubmit,
        setValue: setPasswordFields,
        formState: { errors: passwordChangeErrors }
    } = useForm({ resolver: yupResolver(passwordChangeSchema) });

    const onDrop = useCallback(
        (acceptedFiles) => {
            const file = acceptedFiles[0];
            const blobUrl = URL.createObjectURL(file);
            setPersonalInfo('profileUrl', blobUrl);
            setPersonalInfo('profilePic', file);
        },
        [setPersonalInfo]
    );

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: {
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg'] 
        }
    });

    const onFileSelect = (file) => {
        setPersonalInfo('profileUrl', URL.createObjectURL(file));
        setPersonalInfo('profilePic', file);
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        onFileSelect(file);
    };

    const resetPasswordFields = () => {
        setPasswordFields('currentPassword', '');
        setPasswordFields('newPassword', '');
        setPasswordFields('confirmPassword', '');
    };

    const onPersonalInfoSubmit = data => {
        if(data){
            const formData = new FormData();
            formData.append('first_name', data.firstName);
            formData.append('last_name', data.lastName);
            formData.append('email', data.email);
            if(data.profilePic && typeof data.profilePic !== 'string'){
                formData.append('image', data.profilePic);
            }
            
            dispatch(
                updateProfileDetails({ data: formData })
            ).then(result=>{
                setIsCanceled(false);
            });
            
        }
    };

    const onPasswordChangeSubmit = data => {
        if(data){
            const formData = new FormData();
            formData.append('current_password', data.currentPassword);
            formData.append('new_password', data.newPassword);
            formData.append('confirm_password', data.confirmPassword);

            dispatch(
                updatePassword({ data: formData })
            ).then(()=> {
                resetPasswordFields();
            }).catch(error => console.error('Error in Updaing Password', error));;
        }
    };

    const handleSave = () => {
        handlePersonalInfoSubmit(onPersonalInfoSubmit);
    };

    const handleUpdatePassword = () => {
        handlePasswordChangeSubmit(onPasswordChangeSubmit);
    };

    const handleImageError = (event) => {
        event.target.src = NoImage;
        event.target.alt = 'Image not Found';
    };

    useEffect(()=> {
        if(company.id){
            dispatch(userLoginActivity(
                { companyId: company.id }
            ));
        }
    }, [company.id]);

    useEffect(() => {
        if(userDetails){
            setPersonalInfo('firstName',userDetails?.first_name || '');
            setPersonalInfo('lastName',userDetails?.last_name || '');
            setPersonalInfo('email',userDetails?.email || '');
            setPersonalInfo('name',userDetails?.first_name + ' ' + userDetails?.last_name);
            setPersonalInfo('profileUrl', userDetails?.image_url);
            setPersonalInfo('profilePic', userDetails?.image_url);
            
        }
    },[userDetails, isCanceled]);
    console.log(isCanceled, 'iscsacsdc');
    return (
        <Grid container className="flex-row-space-between" spacing={'24px'}>
            {/* Profile */}
            <Grid item xs={12}>
                <Box className="flex-column gap-4 pb-20px">
                    <Typography className="text-lg-medium gray-900">
                        Profile
                    </Typography>
                </Box>
                <Divider className="border-1-gray" />
            </Grid>

            {/* Personal Info */}
            <Grid item xs={12}>
                <Grid container className='flex-row' spacing={'32px'}>
                    <Grid item xs={4} >
                        <Box className="flex-column">
                            <Typography className="text-sm-medium gray-700" >
                                Personal Info
                            </Typography>
                            <Typography className="text-sm-medium gray-500" >
                                Update your photo and personal details.
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={8} >
                        <Box className="border-1-gray-300 br-16px">
                            <form onSubmit={handlePersonalInfoSubmit(onPersonalInfoSubmit)}>
                                <Box className="padding-24px">
                                    <Box className="flex-row-space-between gap-24">
                                        {/* First Name */}
                                        <Box className="w-50">
                                            <Typography className="text-sm-medium gray-700">
                                                First Name
                                            </Typography>
                                            <input {...personalInfoRegister('firstName')} className='custom-text-field' placeholder='Enter your first name' onChange={(e) => setPersonalInfo('firstName', e.target.value)} />
                                            <span className="text-xs-regular error-500">{personalInfoErrors.firstName?.message}</span>
                                        </Box>
                                        {/* Last Name */}
                                        <Box className="w-50">
                                            <Typography className="text-sm-medium gray-700">
                                                Last Name
                                            </Typography>
                                            <input {...personalInfoRegister('lastName')} className='custom-text-field' placeholder='Enter your last name' onChange={(e) => setPersonalInfo('lastName', e.target.value)} />
                                            <span className="text-xs-regular error-500">{personalInfoErrors.lastName?.message}</span>
                                        </Box>
                                    </Box>
                                    {/* Email */}
                                    <Box className="pt-24px">
                                        <Typography className="text-sm-medium gray-700">
                                            Email
                                        </Typography>
                                        <Box className="flex-row-start-center gap-8 br-8px border-1-gray-300 padding-tb-7-rl-10 bg-disabled-input">
                                            <Mail />
                                            <input
                                                {...personalInfoRegister('email')}
                                                className='border-outline-transition-none w-100 bg-disabled-input'
                                                disabled={true}
                                                placeholder='Enter your email'
                                                onChange={(e) => setPersonalInfo('email', e.target.value)}
                                            />
                                        </Box>
                                        <span className="text-xs-regular error-500">{personalInfoErrors.email?.message}</span>
                                    </Box>

                                    {/* Profile Picture */}
                                    <Box className="flex-row-space-between gap-20 pt-24px">
                                        <Box className="pb-12px text-align-center">
                                            <img
                                                className='br-50'
                                                height="100px"
                                                src={userProfileUrl}
                                                width="100px"
                                                onError={handleImageError}
                                            />
                                        </Box>
                                        <Box className="flex-column-center border-1-gray-300 br-16px padding-tb-16-rl-24 wh-100 icon" {...getRootProps()}>
                                            <Box className="wh-40px bg-gray-100 br-50 border-6-gray-50">
                                                <Upload />
                                            </Box>
                                            <Box className="flex-row gap-4">
                                                <Typography className='text-sm-medium primary-600'>
                                                    Click to upload
                                                </Typography>
                                                <Typography className='text-sm-medium gray-500'>
                                                    or drag and drop
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography className='text-xs-regular gray-500'>
                                                    PNG, JPG or JPEG
                                                </Typography>
                                            </Box>
                                            <input
                                                {...getInputProps()}
                                                id="fileInput"
                                            />

                                            <input
                                                id="profileInput"
                                                style={{ display: 'none' }}
                                                type="file"
                                                onChange={handleFileChange}
                                            />
                                            <span className="text-xs-regular error-500">{personalInfoErrors.profilePic?.message}</span>
                                        </Box>
                                    </Box>
                                </Box>
                                {/* Cancel and Save Button */}
                                <Box className="flex-row-end-center gap-12 padding-tb-16-rl-24 border-top-gray-300">
                                    <CustomButton
                                        handleClick={()=>setIsCanceled(!isCanceled)}
                                        title="Cancel"
                                    />
                                    <CustomButton
                                        buttonColor="#AD509E"
                                        handleClick={()=>handleSave()}
                                        title="Save changes"
                                        titleColor="#FFFFFF"
                                        type="submit"
                                    />
                                </Box>
                            </form>
                        </Box>

                    </Grid>
                </Grid>
            </Grid>

            {/* Password */}
            <Grid item xs={12}>
                <form onSubmit={handlePasswordChangeSubmit(onPasswordChangeSubmit)}>
                    <Box className="flex-column gap-4 pb-20px">
                        <Typography className="text-lg-medium gray-900">
                            Password
                        </Typography>
                        <Typography className="text-sm-regular gray-500">
                            Please enter your current password to change your password.
                        </Typography>
                    </Box>
                    <Divider className="border-1-gray mb-24px" />
                    {Object.entries(passwordChange)?.map(entry => {
                        const [key, value] = entry;
                        return (<>
                            <Grid container className='flex-row' key={key} spacing={'32px'}>
                                <Grid item xs={4}>
                                    <Box>
                                        <Typography className="text-sm-medium gray-700">
                                            {value}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={8}>
                                    <Box className="max-width-70">
                                        <input
                                            {...passwordChangeRegister(key)}
                                            className='custom-text-field'
                                            placeholder={`Enter your ${value}`}
                                            type="password"
                                        />
                                        <span className="text-xs-regular error-500 mt-6px">
                                            {passwordChangeErrors?.[key]?.message}
                                        </span>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Divider className='border-1-gray margin-tb-20' />
                        </>);
                    })}
                    {/* Cancel and Save Button */}
                    <Box className="flex-row-end-center gap-12 padding-tb-0-rl-32">
                        <CustomButton
                            handleClick={()=>resetPasswordFields()}
                            title="Cancel"
                        />
                        <CustomButton
                            buttonColor="#AD509E"
                            handleClick={handleUpdatePassword}
                            title="Update Password"
                            titleColor="#FFFFFF"
                            type="submit"
                        />
                    </Box>
                </form>
            </Grid>

            {/* When you're Logged In */}
            <Grid item xs={12}>
                <Box className="flex-column gap-4 pb-20px">
                    <Typography className="text-lg-medium gray-900">
                        Where you’re logged in
                    </Typography>
                    <Typography className="text-sm-regular gray-500">
                        We’ll alert you via {userDetails?.email} if there is any unusual activity on your account.
                    </Typography>
                </Box>
                <Divider className="border-1-gray mb-4px" />
                {loginActivity && loginActivity.length > 0 && loginActivity?.map((item, index) => {
                    
                    const formattedDate = item.login_time ? format(parseISO(item.login_time), "d MMM 'at' hh:mmaaa") : ' - ';
                    const year = item.login_time ? getYear(parseISO(item.login_time)) : ' - ';
                    
                    if(!item.login_time){
                        return;
                    }

                    return (<><Box className="flex-row-start gap-16 padding-tb-20-rl-16">
                        <Box>
                            <Computer />
                        </Box>
                        <Box className="flex-column gap-5">
                            <Box className="flex-row-start-center gap-8">
                                <Box className="flex-row-align-items-center gap-4">
                                    <Typography className="text-sm-regular gray-500">
                                        {year} 
                                    </Typography>
                                    <Typography className="text-sm-regular gray-500">
                                        {item.device_info}
                                    </Typography>
                                    <Box className="wh-4px br-50 bg-gray-500" />
                                    <Typography className="text-sm-regular gray-500">
                                        {item.browser_info}
                                    </Typography>
                                </Box>
                                {!item.logout_time && <Box className="flex-row-center gap-8 bg-gray-100 br-16px padding-tb-2-r-8-l-6">
                                    <Box className="wh-6px br-50 bg-success-400 "/>
                                    <Typography className="text-xs-medium success-400">
                                        Active now
                                    </Typography>
                                </Box>}
                            </Box>
                            {
                                is_admin && 
                                <Box className="flex-row-align-items-center gap-4">
                                    <Typography className="text-sm-regular gray-500">
                                        {item.name + ', '}
                                    </Typography>
                                    <Box className="wh-4px br-50 bg-gray-500" />
                                    <Typography className="text-sm-regular gray-500">
                                        {item.email}
                                    </Typography>
                                </Box>
                            }
                            
                            <Box className="flex-row-align-items-center gap-4">
                                <Typography className="text-sm-regular gray-500">
                                    {item.location}
                                </Typography>
                                <Box className="wh-4px br-50 bg-gray-500" />
                                <Typography className="text-sm-regular gray-500">
                                    {formattedDate}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    <Divider className="border-1-gray"/>
                    </>);
                    
                })
                }
            </Grid>
        </Grid>
    );
}

export default Profile;